import { CloudOffOutlined, HourglassEmptyOutlined, LinkOff, LockOutlined } from 'components/icons';
import AuthFooter from 'components/cards/AuthFooter';
import LogoMain from 'components/logo/LogoMain';
import EnvIndicatorBar from 'layout/MainLayout/Header/EnvIndicatorBar';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, buttonVariants } from 'components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { Skeleton } from 'components/ui/skeleton';
import useAuth from 'hooks/useAuth';

/**
 * portal-be's authorization endpoint redirects an authenticated, consent-required request here
 * with `client_id` and `state` — `state` is a server-generated, opaque lookup token for the
 * in-flight request, not the client's own. The redirect also carries a `scope` parameter, which
 * this page deliberately ignores: whoever builds the link controls it, so the scopes shown and
 * approved come only from the session endpoint below.
 */
const AUTHORIZE_URL = `${import.meta.env.VITE_API_URL}/oauth2/authorize`;

/**
 * `ConsentRedirectFilter` on portal-be binds a session-bound CSRF token and the request's
 * `client_id` the moment it redirects here, and this endpoint hands both back — with the pending
 * request's own scopes, redirect host and token lifetime, all read server-side — over an
 * authenticated, credentialed fetch. None of it travels as a URL query parameter: the CSRF token
 * would otherwise sit in browser history, `Referer` headers and access logs, and anything else on
 * the URL could be set to anything by whoever constructs the link.
 */
const CONSENT_SESSION_URL = `${import.meta.env.VITE_API_URL}/v1/oauth2/consent/session`;

const SUPPORT_EMAIL = 'support@crespect.com';

/** Long enough for a slow network, short enough that nobody stares at a skeleton wondering. */
const SESSION_FETCH_TIMEOUT_MS = 15_000;

type ScopeKind = 'identity' | 'read' | 'write' | 'delete' | 'other';

interface RequestedScope {
    scope: string;
    kind: ScopeKind;
    domain: string | null;
}

interface AccessDuration {
    expiresAfterSeconds: number;
    renewsWhileUsed: boolean;
    maxLifetimeSeconds: number | null;
}

interface ConsentSession {
    csrfToken: string;
    clientId: string;
    clientName: string | null;
    scopes: RequestedScope[];
    requestedAt: string | null;
    firmName: string | null;
    access: AccessDuration | null;
}

type SessionFailure = 'signed-out' | 'expired' | 'unavailable';

/** One requested area, with the scope string behind each kind of access it asked for. */
interface AreaRequest {
    domain: string;
    readScope: string | null;
    writeScope: string | null;
    deleteScope: string | null;
}

function groupByArea(scopes: RequestedScope[]): AreaRequest[] {
    const byDomain = new Map<string, AreaRequest>();
    scopes.forEach(({ scope, kind, domain }) => {
        if ((kind !== 'read' && kind !== 'write' && kind !== 'delete') || !domain) {
            return;
        }
        const entry = byDomain.get(domain) ?? { domain, readScope: null, writeScope: null, deleteScope: null };
        if (kind === 'read') {
            entry.readScope = scope;
        } else if (kind === 'write') {
            entry.writeScope = scope;
        } else {
            entry.deleteScope = scope;
        }
        byDomain.set(domain, entry);
    });
    return [...byDomain.values()].toSorted((a, b) => a.domain.localeCompare(b.domain));
}

/**
 * Every area's view and change start ticked; delete, which cannot be undone, and an unrecognised
 * scope start unticked.
 */
function defaultSelection(areas: AreaRequest[]): Set<string> {
    return new Set(
        areas.flatMap(({ readScope, writeScope }) =>
            [readScope, writeScope].filter((scope): scope is string => scope !== null)
        )
    );
}

function failureFor(status: number): SessionFailure {
    if (status === 401 || status === 403) {
        return 'signed-out';
    }
    if (status === 404) {
        return 'expired';
    }
    return 'unavailable';
}

/**
 * The environment banner and footer links of the sign-in pages around a card with the Crespect
 * logo centred at its top, so the user can see which Crespect they are granting access to before
 * they read anything else.
 */
function PageFrame({ children, busy = false }: { children: ReactNode; busy?: boolean }): JSX.Element {
    return (
        <div className="bg-background flex min-h-dvh flex-col">
            <EnvIndicatorBar />
            <main aria-busy={busy} className="flex flex-1 justify-center px-4 py-8 max-sm:px-0 sm:py-16">
                <Card className="h-fit w-full max-w-lg max-sm:rounded-none max-sm:shadow-none max-sm:ring-0">
                    <div className="flex justify-center px-(--card-spacing)">
                        <LogoMain />
                    </div>
                    {children}
                </Card>
            </main>
            <div className="px-6 pb-6">
                <AuthFooter />
            </div>
        </div>
    );
}

/** Focuses the page heading once it appears, so a screen reader starts at the question being asked. */
function FocusedHeading({ children }: { children: ReactNode }): JSX.Element {
    const headingRef = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        headingRef.current?.focus();
    }, []);
    return (
        <CardTitle>
            <h1
                ref={headingRef}
                tabIndex={-1}
                className="text-foreground text-lg font-medium text-balance outline-none"
            >
                {children}
            </h1>
        </CardTitle>
    );
}

const FAILURE_ICON: Record<SessionFailure | 'missing', React.ElementType> = {
    missing: LinkOff,
    expired: HourglassEmptyOutlined,
    'signed-out': LockOutlined,
    unavailable: CloudOffOutlined,
};

interface FailureCardProps {
    failure: SessionFailure | 'missing';
    onRetry?: () => void;
}

/**
 * Crespect's error-state shape: an icon, what happened, the one action that fixes it as the amber
 * primary, and a way to reach support. Every failure also leads back into Crespect — the user
 * arrived here from another app and may have no Crespect tab open.
 */
function FailureCard({ failure, onRetry }: FailureCardProps): JSX.Element {
    const Icon = FAILURE_ICON[failure];
    const backToCrespectIsPrimary = failure === 'missing' || failure === 'expired';
    return (
        <PageFrame>
            <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
                <Icon className="text-muted-foreground size-12" aria-hidden />
                <FocusedHeading>
                    <FormattedMessage id={`oauth-consent.failure.${failure}.title`} />
                </FocusedHeading>
                <p className="text-foreground max-w-md text-sm">
                    <FormattedMessage id={`oauth-consent.failure.${failure}.body`} />
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {failure === 'signed-out' && (
                        <Link to="/login" className={buttonVariants({ variant: 'secondary' })}>
                            <FormattedMessage id="oauth-consent.failure.sign-in" />
                        </Link>
                    )}
                    {failure === 'unavailable' && onRetry && (
                        <Button variant="secondary" onClick={onRetry}>
                            <FormattedMessage id="oauth-consent.failure.retry" />
                        </Button>
                    )}
                    <Link
                        to="/"
                        className={buttonVariants({ variant: backToCrespectIsPrimary ? 'secondary' : 'outline' })}
                    >
                        <FormattedMessage id="oauth-consent.failure.back-to-crespect" />
                    </Link>
                </div>
                <div className="border-border mt-5 w-full max-w-md border-t pt-4 text-xs">
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-link font-medium hover:underline">
                        <FormattedMessage id="oauth-consent.failure.contact-support" />
                    </a>
                </div>
            </CardContent>
        </PageFrame>
    );
}

/** Shaped like the loaded page, so nothing jumps when the request arrives. */
function LoadingCard(): JSX.Element {
    return (
        <PageFrame busy>
            <CardHeader className="gap-3">
                <p className="sr-only" role="status">
                    <FormattedMessage id="oauth-consent.loading" />
                </p>
                <Skeleton className="h-7 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <Skeleton className="h-20 w-full" />
                {[0, 1, 2, 3].map((row) => (
                    <Skeleton key={row} className="h-8 w-full" />
                ))}
            </CardContent>
            <CardFooter className="justify-end gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-28" />
            </CardFooter>
        </PageFrame>
    );
}

function durationPhrase(seconds: number): JSX.Element {
    const days = Math.floor(seconds / 86_400);
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (days >= 1) {
        return <FormattedMessage key="days" id="oauth-consent.duration.days" values={{ count: days }} />;
    }
    if (hours >= 1) {
        return <FormattedMessage key="hours" id="oauth-consent.duration.hours" values={{ count: hours }} />;
    }
    return <FormattedMessage key="minutes" id="oauth-consent.duration.minutes" values={{ count: minutes }} />;
}

function DurationNote({ access }: { access: AccessDuration }): JSX.Element {
    const idle = durationPhrase(access.expiresAfterSeconds);
    if (access.renewsWhileUsed && access.maxLifetimeSeconds) {
        return (
            <FormattedMessage
                id="oauth-consent.duration.renewing-capped"
                values={{ idle, max: durationPhrase(access.maxLifetimeSeconds) }}
            />
        );
    }
    return (
        <FormattedMessage
            id={access.renewsWhileUsed ? 'oauth-consent.duration.renewing' : 'oauth-consent.duration.fixed'}
            values={{ duration: idle }}
        />
    );
}

/** "cases" reads as a sentence; an area whose name carries its own capitals keeps them. */
function inSentence(domain: string): string {
    return /[A-Z]/.test(domain.slice(1)) ? domain : domain.charAt(0).toLowerCase() + domain.slice(1);
}

interface ScopeChoice {
    scope: string;
    label: ReactNode;
}

interface ScopeListProps {
    appName: string;
    areas: AreaRequest[];
    otherScopes: string[];
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
}

/**
 * One checkbox per requested scope, each spelled out as what the app would be able to do. Changing
 * records needs viewing them, and deleting needs changing them, so ticking a box ticks every box
 * beneath it in that chain, and clearing a box clears every box above it: a token that can delete
 * what it cannot change, or change what it cannot see, is never what anyone means to grant.
 */
function ScopeList({ appName, areas, otherScopes, selected, onChange }: ScopeListProps): JSX.Element {
    const intl = useIntl();
    const headingId = useId();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scroll, setScroll] = useState({ above: false, below: false });

    const measure = useCallback(() => {
        const element = scrollRef.current;
        if (!element) {
            return;
        }
        const above = element.scrollTop > 1;
        const below = element.scrollTop + element.clientHeight < element.scrollHeight - 1;
        setScroll((previous) => (previous.above === above && previous.below === below ? previous : { above, below }));
    }, []);

    useEffect(() => {
        measure();
        const element = scrollRef.current;
        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [measure]);

    const toggle = (scope: string, on: boolean) => {
        const next = new Set(selected);
        const area = areas.find(
            ({ readScope, writeScope, deleteScope }) =>
                readScope === scope || writeScope === scope || deleteScope === scope
        );
        if (on) {
            next.add(scope);
            // Ticking delete needs change, and change needs view: pull in whichever of those this
            // area still has unticked.
            if (area?.deleteScope === scope) {
                if (area.writeScope) {
                    next.add(area.writeScope);
                }
                if (area.readScope) {
                    next.add(area.readScope);
                }
            } else if (area?.writeScope === scope && area.readScope) {
                next.add(area.readScope);
            }
        } else {
            next.delete(scope);
            // Clearing view clears everything built on top of it; clearing change clears delete,
            // which is built on change.
            if (area?.readScope === scope) {
                if (area.writeScope) {
                    next.delete(area.writeScope);
                }
                if (area.deleteScope) {
                    next.delete(area.deleteScope);
                }
            } else if (area?.writeScope === scope && area.deleteScope) {
                next.delete(area.deleteScope);
            }
        }
        onChange(next);
    };

    const choices: ScopeChoice[] = [
        ...areas.flatMap(({ domain, readScope, writeScope, deleteScope }) => {
            const values = { area: inSentence(domain) };
            return [
                ...(readScope
                    ? [{ scope: readScope, label: intl.formatMessage({ id: 'oauth-consent.scope.view' }, values) }]
                    : []),
                ...(writeScope
                    ? [{ scope: writeScope, label: intl.formatMessage({ id: 'oauth-consent.scope.change' }, values) }]
                    : []),
                ...(deleteScope
                    ? [{ scope: deleteScope, label: intl.formatMessage({ id: 'oauth-consent.scope.delete' }, values) }]
                    : []),
            ];
        }),
        ...otherScopes.map((scope) => ({
            scope,
            label: intl.formatMessage({ id: 'oauth-consent.scope.generic' }, { scope }),
        })),
    ];

    return (
        <section aria-labelledby={headingId} className="border-border overflow-hidden rounded-lg border">
            <div className="border-border bg-muted/40 border-b px-3 py-2">
                <h2 id={headingId} className="text-foreground text-sm font-medium">
                    {/*
                     * The app's name as it registered it: a JSX text child, so a hostile name stays inert
                     * text, and `dir="auto"` keeps its direction from spilling into the sentence.
                     */}
                    <FormattedMessage
                        id="oauth-consent.selection.heading"
                        values={{
                            app: (
                                <span key="app" dir="auto" className="font-semibold break-words">
                                    {appName}
                                </span>
                            ),
                        }}
                    />
                </h2>
            </div>
            <div className="relative">
                <div
                    ref={scrollRef}
                    onScroll={measure}
                    tabIndex={0}
                    className="focus-visible:ring-ring/50 max-h-56 overflow-y-auto px-3 outline-none focus-visible:ring-2"
                >
                    <fieldset className="divide-border flex flex-col divide-y">
                        <legend className="sr-only">
                            <FormattedMessage id="oauth-consent.selection.heading" values={{ app: appName }} />
                        </legend>
                        {choices.map(({ scope, label }) => (
                            <label
                                key={scope}
                                className="flex min-h-10 cursor-pointer items-center gap-3 py-1 text-sm break-words"
                            >
                                <Checkbox
                                    variant="secondary"
                                    checked={selected.has(scope)}
                                    onCheckedChange={(checked) => toggle(scope, checked === true)}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </fieldset>
                </div>
                {/* Fades over the edge that has more behind it, so the box reads as scrollable even
                    where the platform hides scrollbars until you scroll. */}
                {scroll.above && (
                    <div className="from-card pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b to-transparent" />
                )}
                {scroll.below && (
                    <div className="from-card pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent" />
                )}
            </div>
        </section>
    );
}

interface ConsentRequestProps {
    clientId: string;
    state: string;
}

/**
 * The OAuth consent screen a user sees, already logged in, once its session is resolved.
 * `OAuthConsentPage` below remounts this component (via `key`) whenever the `client_id`/`state`
 * pair changes, so a new request always starts from a clean state instead of carrying over the
 * previous request's.
 *
 * Two independent native `<form>` elements (not a `fetch` submit) so the browser performs a real
 * top-level navigation and follows portal-be's redirect all the way to the client's own
 * `redirect_uri` — which is frequently on a different, non-CORS-reachable origin. The session
 * cookie rides along under the same cross-origin-but-same-site rule the rest of the login flow
 * relies on.
 *
 * Approving posts back only the scopes the user ticked, plus the identity scope; portal-be grants
 * that subset and never anything the request did not ask for. Cancelling posts back none, which
 * portal-be turns into an `access_denied` redirect to the client.
 */
function ConsentRequest({ clientId, state }: ConsentRequestProps): JSX.Element {
    const { user, logout } = useAuth();
    const [session, setSession] = useState<ConsentSession | null>(null);
    const [failure, setFailure] = useState<SessionFailure | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SESSION_FETCH_TIMEOUT_MS);
        let settled = false;
        fetch(`${CONSENT_SESSION_URL}?state=${encodeURIComponent(state)}`, {
            credentials: 'include',
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    settled = true;
                    setFailure(failureFor(response.status));
                    return;
                }
                return (response.json() as Promise<ConsentSession>).then((body) => {
                    settled = true;
                    setSelected(defaultSelection(groupByArea(body.scopes)));
                    setSession(body);
                });
            })
            .catch(() => {
                if (!settled) {
                    settled = true;
                    setFailure('unavailable');
                }
            })
            .finally(() => clearTimeout(timeout));
        return () => {
            // Mark first so the abort this triggers is not reported as a failure of a request
            // nobody is waiting for any more.
            settled = true;
            clearTimeout(timeout);
            controller.abort();
        };
    }, [state, attempt]);

    const retry = useCallback(() => {
        setFailure(null);
        setAttempt((previous) => previous + 1);
    }, []);

    const areas = useMemo(() => (session ? groupByArea(session.scopes) : []), [session]);

    if (failure) {
        return <FailureCard failure={failure} onRetry={retry} />;
    }

    if (!session) {
        return <LoadingCard />;
    }

    // The resolved session must belong to this exact request. portal-be already answers 404 for a
    // mismatched pair; this is the last line of defense against ever rendering one client's
    // approval forms under another's resolved identity.
    if (session.clientId !== clientId) {
        return <FailureCard failure="expired" />;
    }

    const { csrfToken } = session;
    const appName = session.clientName?.trim() || session.clientId;
    const requested = session.scopes.map(({ scope }) => scope);
    const identityScopes = session.scopes.filter(({ kind }) => kind === 'identity').map(({ scope }) => scope);
    const otherScopes = session.scopes.filter(({ kind }) => kind === 'other').map(({ scope }) => scope);
    const approvedScopes = [...identityScopes, ...requested.filter((scope) => selected.has(scope))];
    const nothingSelected = !requested.some((scope) => selected.has(scope));
    const signedInName = user ? `${user.firstName} ${user.lastName}`.trim() || user.name : null;

    return (
        <PageFrame>
            <CardHeader className="justify-items-center gap-2 text-center">
                <FocusedHeading>
                    <FormattedMessage id="oauth-consent.title" />
                </FocusedHeading>
                {signedInName && (
                    <p className="text-muted-foreground text-sm">
                        <FormattedMessage
                            id={session.firmName ? 'oauth-consent.signed-in-at-firm' : 'oauth-consent.signed-in'}
                            values={{
                                name: (
                                    <span key="name" className="text-foreground font-medium">
                                        {signedInName}
                                    </span>
                                ),
                                firm: session.firmName,
                            }}
                        />
                        {' · '}
                        <button
                            type="button"
                            onClick={logout}
                            className="text-link cursor-pointer font-medium hover:underline"
                        >
                            <FormattedMessage id="oauth-consent.not-you" />
                        </button>
                    </p>
                )}
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
                <ScopeList
                    appName={appName}
                    areas={areas}
                    otherScopes={otherScopes}
                    selected={selected}
                    onChange={setSelected}
                />

                <p className="text-muted-foreground text-xs">
                    {identityScopes.length > 0 && (
                        <>
                            <FormattedMessage id="oauth-consent.scope.openid" />{' '}
                        </>
                    )}
                    {session.access && <DurationNote access={session.access} />}
                </p>
            </CardContent>

            <CardFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm" aria-live="polite">
                    {nothingSelected && (
                        <span className="text-destructive">
                            <FormattedMessage id="oauth-consent.nothing-selected" />
                        </span>
                    )}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <form action={AUTHORIZE_URL} method="post">
                        <input type="hidden" name="client_id" value={session.clientId} />
                        <input type="hidden" name="state" value={state} />
                        <input type="hidden" name="_csrf" value={csrfToken} />
                        <Button type="submit" variant="outline" className="w-full sm:w-auto">
                            <FormattedMessage id="oauth-consent.decline" />
                        </Button>
                    </form>
                    <form action={AUTHORIZE_URL} method="post">
                        <input type="hidden" name="client_id" value={session.clientId} />
                        <input type="hidden" name="state" value={state} />
                        <input type="hidden" name="_csrf" value={csrfToken} />
                        {approvedScopes.map((scope) => (
                            <input key={scope} type="hidden" name="scope" value={scope} />
                        ))}
                        <Button
                            type="submit"
                            variant="secondary"
                            className="w-full sm:w-auto"
                            disabled={nothingSelected}
                        >
                            <FormattedMessage id="oauth-consent.approve" />
                        </Button>
                    </form>
                </div>
            </CardFooter>
        </PageFrame>
    );
}

/**
 * Hands a well-formed `client_id`/`state` pair to `ConsentRequest`, keyed by that pair so a
 * changed request always gets a fresh component instance rather than one that carries over the
 * previous request's session.
 */
const OAuthConsentPage = (): JSX.Element => {
    const [searchParams] = useSearchParams();
    const clientId = searchParams.get('client_id');
    const state = searchParams.get('state');

    if (!clientId || !state) {
        return <FailureCard failure="missing" />;
    }

    return <ConsentRequest key={`${clientId}:${state}`} clientId={clientId} state={state} />;
};

export default OAuthConsentPage;
